import { useEffect, useMemo, useRef } from 'react'

import styled from 'styled-components'
import * as THREE from 'three'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader'

import { ReviewViewpointStl } from '../../../api/reviewTasks'
import { FloorProgressTranslation } from '../../../api/types'
import {
  getCadHeight,
  getPrimaryToSecondary,
  getProgressRotation,
} from '../../../utils/progressFloorTransform'
import {
  computeProgressPanoCameraPose,
  hfovToVerticalFovDeg,
  progressPanoLookTarget,
} from '../../../utils/progressPanoCamera'
import { ViewerPosition } from '../image_viewer/types'

const STL_LOAD_CONCURRENCY = 6
const STL_RETRIES = 2
const STL_RETRY_BASE_MS = 400
/** Cull meshes whose closest AABB point is farther than this (CAD units). */
const STL_MAX_DISTANCE = 25000
/** Shorter far plane so distant wall segments don't dominate the view. */
const STL_CAMERA_FAR = 80000
const STL_CAMERA_NEAR = 0.5

const geometryCache: Map<string, Promise<THREE.BufferGeometry>> = new Map()

function urlCacheKey(url: string): string {
  try {
    const u = new URL(url, window.location.origin)
    return `${u.origin}${u.pathname}`
  } catch {
    return url
  }
}

function loadStlGeometry(
  loader: STLLoader,
  url: string,
  isCancelled: () => boolean,
): Promise<THREE.BufferGeometry> {
  const key = urlCacheKey(url)
  const cached = geometryCache.get(key)
  if (cached) {
    return cached.then(g => g.clone())
  }

  const fetchOnce = (): Promise<THREE.BufferGeometry> =>
    new Promise((resolve, reject) => {
      loader.load(
        url,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- three@0.131 STLLoader typings
        (rawGeometry: any) => {
          resolve(rawGeometry as THREE.BufferGeometry)
        },
        undefined,
        err => {
          reject(err ?? new Error('STL load failed'))
        },
      )
    })

  const withRetry = async (): Promise<THREE.BufferGeometry> => {
    let lastErr: unknown
    for (let attempt = 0; attempt <= STL_RETRIES; attempt += 1) {
      if (isCancelled()) {
        throw new Error('cancelled')
      }
      try {
        const g = await fetchOnce()
        g.computeVertexNormals()
        return g
      } catch (err) {
        lastErr = err
        if (attempt < STL_RETRIES) {
          await new Promise(r => setTimeout(r, STL_RETRY_BASE_MS * 2 ** attempt))
        }
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error('STL load failed')
  }

  const promise = withRetry()
  geometryCache.set(key, promise)
  promise.catch(() => geometryCache.delete(key))
  return promise.then(g => g.clone())
}

async function mapPool<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>,
): Promise<void> {
  let index = 0
  const worker = async () => {
    while (index < items.length) {
      const current = items[index]
      index += 1
      await fn(current)
    }
  }
  const n = Math.max(1, Math.min(limit, items.length))
  await Promise.all(Array.from({ length: n }, () => worker()))
}

function disposeGroup(group: THREE.Group): void {
  const children = [...group.children]
  for (const child of children) {
    group.remove(child)
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose()
      const mat = child.material
      if (Array.isArray(mat)) {
        mat.forEach(m => m.dispose())
      } else {
        mat?.dispose()
      }
    }
  }
}

function wireframeMaterial(color: string): THREE.MeshBasicMaterial {
  let threeColor: THREE.Color
  try {
    threeColor = new THREE.Color(color)
  } catch {
    threeColor = new THREE.Color('#4fc3f7')
  }
  return new THREE.MeshBasicMaterial({
    color: threeColor,
    wireframe: true,
    transparent: true,
    opacity: 0.9,
    depthTest: true,
    depthWrite: false,
  })
}

const OverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  overflow: hidden;

  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
`

export type ReviewStlOverlayProps = {
  enabled: boolean
  meshes: ReviewViewpointStl[]
  /** Plan-pixel robot position for the current viewpoint. */
  planX: number | null
  planY: number | null
  imageAngle: number
  progressTranslation: FloorProgressTranslation | null | undefined
  viewerPosition: ViewerPosition
  /** Plan-space nudge applied to the STL group (Control+drag / steppers). */
  offsetX?: number
  offsetY?: number
}

export const ReviewStlOverlay = ({
  enabled,
  meshes,
  planX,
  planY,
  imageAngle,
  progressTranslation,
  viewerPosition,
  offsetX = 0,
  offsetY = 0,
}: ReviewStlOverlayProps) => {
  const mountRef = useRef<HTMLDivElement | null>(null)
  const runtimeRef = useRef<{
    renderer: THREE.WebGLRenderer
    scene: THREE.Scene
    camera: THREE.PerspectiveCamera
    stlGroup: THREE.Group
    loader: STLLoader
    disposed: boolean
  } | null>(null)

  const affine = useMemo(
    () => getPrimaryToSecondary(progressTranslation),
    [progressTranslation],
  )
  const cadHeight = useMemo(
    () => getCadHeight(progressTranslation),
    [progressTranslation],
  )
  const rotation = useMemo(
    () => getProgressRotation(progressTranslation),
    [progressTranslation],
  )

  const cameraPose = useMemo(() => {
    if (
      !enabled ||
      affine == null ||
      cadHeight == null ||
      planX == null ||
      planY == null ||
      !Number.isFinite(planX) ||
      !Number.isFinite(planY)
    ) {
      return null
    }
    return computeProgressPanoCameraPose({
      planX,
      planY,
      affine,
      cadHeight,
      imageAngle,
      rotation,
    })
  }, [affine, cadHeight, enabled, imageAngle, planX, planY, rotation])

  const stlJobs = useMemo(() => {
    const byUrl = new Map<string, ReviewViewpointStl>()
    for (const mesh of meshes) {
      if (!mesh.stl_url) continue
      if (!byUrl.has(mesh.stl_url)) {
        byUrl.set(mesh.stl_url, mesh)
      }
    }
    return Array.from(byUrl.entries()).map(([url, mesh]) => ({ url, mesh }))
  }, [meshes])

  // Init / dispose WebGL
  useEffect(() => {
    if (!enabled || !cameraPose) {
      return
    }
    const mount = mountRef.current
    if (!mount) {
      return
    }

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(
      60,
      1,
      STL_CAMERA_NEAR,
      STL_CAMERA_FAR,
    )
    camera.up.set(0, 0, 1)
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
    })
    renderer.setClearColor(0x000000, 0)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    mount.appendChild(renderer.domElement)

    const stlGroup = new THREE.Group()
    scene.add(stlGroup)
    scene.add(new THREE.AmbientLight(0xffffff, 0.85))

    const runtime = {
      renderer,
      scene,
      camera,
      stlGroup,
      loader: new STLLoader(),
      disposed: false,
    }
    runtimeRef.current = runtime

    const resize = () => {
      if (runtime.disposed) return
      const w = mount.clientWidth || 1
      const h = mount.clientHeight || 1
      runtime.camera.aspect = w / h
      runtime.camera.updateProjectionMatrix()
      runtime.renderer.setSize(w, h, false)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(mount)

    let raf = 0
    const tick = () => {
      if (runtime.disposed) return
      runtime.renderer.render(runtime.scene, runtime.camera)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      runtime.disposed = true
      cancelAnimationFrame(raf)
      ro.disconnect()
      disposeGroup(stlGroup)
      renderer.dispose()
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement)
      }
      if (runtimeRef.current === runtime) {
        runtimeRef.current = null
      }
    }
  }, [cameraPose, enabled])

  // Load viewpoint STLs
  useEffect(() => {
    const runtime = runtimeRef.current
    if (!enabled || !cameraPose || !runtime || runtime.disposed) {
      return
    }

    let cancelled = false
    const isCancelled = () => cancelled || runtime.disposed

    disposeGroup(runtime.stlGroup)

    void mapPool(stlJobs, STL_LOAD_CONCURRENCY, async ({ url, mesh }) => {
      if (isCancelled()) return
      try {
        const geometry = await loadStlGeometry(runtime.loader, url, isCancelled)
        if (isCancelled()) {
          geometry.dispose()
          return
        }
        geometry.computeBoundingBox()
        const box = geometry.boundingBox
        if (box && cameraPose) {
          const closest = box.clampPoint(
            new THREE.Vector3(
              cameraPose.position.x,
              cameraPose.position.y,
              cameraPose.position.z,
            ),
            new THREE.Vector3(),
          )
          const dist = closest.distanceTo(
            new THREE.Vector3(
              cameraPose.position.x,
              cameraPose.position.y,
              cameraPose.position.z,
            ),
          )
          if (dist > STL_MAX_DISTANCE) {
            geometry.dispose()
            return
          }
        }
        const color = mesh.display_color || '#4fc3f7'
        const threeMesh = new THREE.Mesh(geometry, wireframeMaterial(color))
        threeMesh.userData.itemId = mesh.item_id
        runtime.stlGroup.add(threeMesh)
      } catch {
        // Skip failed STLs (CORS, missing file, cancelled).
      }
    })

    return () => {
      cancelled = true
    }
  }, [cameraPose, enabled, stlJobs])

  // Sync camera to Pannellum look; apply plan-space STL nudge on the group.
  useEffect(() => {
    const runtime = runtimeRef.current
    if (!enabled || !cameraPose || !runtime || runtime.disposed) {
      return
    }
    const { position } = cameraPose
    runtime.camera.position.set(position.x, position.y, position.z)
    const target = progressPanoLookTarget(
      position,
      viewerPosition.yaw,
      viewerPosition.pitch,
      rotation,
    )
    runtime.camera.lookAt(new THREE.Vector3(target.x, target.y, target.z))
    const aspect =
      (runtime.renderer.domElement.clientWidth || 1) /
      (runtime.renderer.domElement.clientHeight || 1)
    runtime.camera.fov = hfovToVerticalFovDeg(viewerPosition.hfov, aspect)
    runtime.camera.aspect = aspect
    runtime.camera.updateProjectionMatrix()
    // Offset meshes in plan XY (CAD Z-up); camera stays at the viewpoint.
    runtime.stlGroup.position.set(offsetX, offsetY, 0)
  }, [cameraPose, enabled, offsetX, offsetY, rotation, viewerPosition])

  if (!enabled || !cameraPose) {
    return null
  }

  return <OverlayRoot ref={mountRef} aria-hidden />
}
